import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, NgModule } from '@angular/core';
import * as raygun4js_ from 'raygun4js';
import { getUser } from '@play/state';
import { Store } from '@ngrx/store';
import { CommonModule } from '@angular/common';

/**
 * Error reporting API key
 */
const RAYGUN_API_KEY = new InjectionToken("error-reporting.api-key");
/**
 * Application Version for filtering in the error reporting backend.
 */
const APPLICATION_VERSION = new InjectionToken("error-reporting.version");
/**
 * Domains that are allowed to report JavaScript errors
 */
const WHITELISTED_DOMAINS = new InjectionToken("error-reporting.whitelisted-domains");
/**
 * Object keys that should not be sent to the error reporting backend
 */
const FILTERED_KEYS = new InjectionToken("error-reporting.filtered-keys");

const raygun4js$2 = raygun4js_; // for FESM
class ErrorReporter {
    constructor(injector, version, apiKey, whitelistedDomains, filteredKeys) {
        this.injector = injector;
        this.version = version;
        this.apiKey = apiKey;
        this.whitelistedDomains = whitelistedDomains;
        this.filteredKeys = filteredKeys;
        this.setupRaygun();
        this.setupUser();
    }
    handleError(error) {
        const originalError = error.originalError;
        raygun4js$2("send", {
            error: originalError || error,
        });
        console.error(originalError || error);
    }
    setupUser() {
        setTimeout(() => {
            this.injector
                .get(Store)
                .select(getUser)
                .subscribe(user => {
                if (user.entry) {
                    raygun4js$2("setUser", {
                        identifier: user.entry.id,
                        isAnonymous: false,
                    });
                }
                else {
                    raygun4js$2("setUser", {
                        isAnonymous: true,
                    });
                }
            });
        }, 0);
    }
    setupRaygun() {
        raygun4js$2("apiKey", this.apiKey);
        raygun4js$2("setVersion", this.version);
        raygun4js$2("options", {
            ignore3rdPartyErrors: true,
            disableAnonymousUserTracking: true,
        });
        raygun4js$2("whitelistCrossOriginDomains", this.whitelistedDomains);
        raygun4js$2("saveIfOffline", true);
        raygun4js$2("filterSensitiveData", this.filteredKeys);
        raygun4js$2("setFilterScope", "all");
    }
}
ErrorReporter.ɵfac = function ErrorReporter_Factory(t) { return new (t || ErrorReporter)(i0.ɵɵinject(i0.Injector), i0.ɵɵinject(APPLICATION_VERSION), i0.ɵɵinject(RAYGUN_API_KEY), i0.ɵɵinject(WHITELISTED_DOMAINS), i0.ɵɵinject(FILTERED_KEYS)); };
ErrorReporter.ɵprov = i0.ɵɵdefineInjectable({ token: ErrorReporter, factory: ErrorReporter.ɵfac, providedIn: "root" });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ErrorReporter, [{
        type: Injectable,
        args: [{
                providedIn: "root",
            }]
    }], function () { return [{ type: i0.Injector }, { type: undefined, decorators: [{
                type: Inject,
                args: [APPLICATION_VERSION]
            }] }, { type: undefined, decorators: [{
                type: Inject,
                args: [RAYGUN_API_KEY]
            }] }, { type: undefined, decorators: [{
                type: Inject,
                args: [WHITELISTED_DOMAINS]
            }] }, { type: undefined, decorators: [{
                type: Inject,
                args: [FILTERED_KEYS]
            }] }]; }, null); })();

const raygun4js$1 = raygun4js_; // for FESM
function breadcrumbsReducer(reducer) {
    raygun4js$1("disableAutoBreadcrumbsConsole");
    raygun4js$1("disableAutoBreadcrumbsXHR");
    return (state, action) => {
        raygun4js$1("recordBreadcrumb", {
            message: `Action Dispatched: ${action.type}`,
            metadata: action,
            level: "info",
            location: "breadcrumbsReducer:breadcrumbsReducer",
        });
        return reducer(state, action);
    };
}

const normalizeFilename = (filename) => {
    return filename.split("?")[0];
};
const beforeSend = payload => {
    const stacktrace = payload.Details.Error.StackTrace;
    for (let i = 0; i < stacktrace.length; i++) {
        const stackline = stacktrace[i];
        stackline.FileName = normalizeFilename(stackline.FileName);
    }
    return payload;
};

const raygun4js = raygun4js_;
class ErrorHandlingModule {
    constructor() {
        raygun4js("onBeforeSend", beforeSend);
    }
}
ErrorHandlingModule.ɵfac = function ErrorHandlingModule_Factory(t) { return new (t || ErrorHandlingModule)(); };
ErrorHandlingModule.ɵmod = i0.ɵɵdefineNgModule({ type: ErrorHandlingModule });
ErrorHandlingModule.ɵinj = i0.ɵɵdefineInjector({ providers: [
        ErrorReporter,
        {
            provide: WHITELISTED_DOMAINS,
            useValue: "cdn.eslgaming.com",
            multi: true,
        },
        {
            provide: FILTERED_KEYS,
            useValue: ["password", "email", "firstname", "lastname"],
        },
        {
            provide: RAYGUN_API_KEY,
            useValue: "",
        },
        {
            provide: APPLICATION_VERSION,
            useValue: "0.0.0",
        },
    ], imports: [[CommonModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(ErrorHandlingModule, { imports: [CommonModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ErrorHandlingModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule],
                providers: [
                    ErrorReporter,
                    {
                        provide: WHITELISTED_DOMAINS,
                        useValue: "cdn.eslgaming.com",
                        multi: true,
                    },
                    {
                        provide: FILTERED_KEYS,
                        useValue: ["password", "email", "firstname", "lastname"],
                    },
                    {
                        provide: RAYGUN_API_KEY,
                        useValue: "",
                    },
                    {
                        provide: APPLICATION_VERSION,
                        useValue: "0.0.0",
                    },
                ],
            }]
    }], function () { return []; }, null); })();

/*
 * Public API Surface of error-reporting
 */

/**
 * Generated bundle index. Do not edit.
 */

export { APPLICATION_VERSION, ErrorHandlingModule, ErrorReporter, FILTERED_KEYS, RAYGUN_API_KEY, WHITELISTED_DOMAINS, breadcrumbsReducer };
//# sourceMappingURL=play-error-reporting.js.map
